"use client";

import { useEffect, useState } from "react";

// A deploy swaps the JS chunks; a page opened before it then fails to lazy-load
// the next piece (e.g. the onboarding session form). One reload picks up the new
// build; a per-page timestamp stops a loop if the reload does not help.
const CHUNK_ERROR = /ChunkLoadError|Loading (CSS )?chunk|Failed to load chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const RELOAD_FLAG = "ama:chunk-reload";

export default function RootError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const isChunkError = CHUNK_ERROR.test(`${error?.name} ${error?.message}`);
  const [reloading, setReloading] = useState(isChunkError);

  useEffect(() => {
    console.error("Page error:", error);
    if (!isChunkError) return;
    try {
      const key = `${RELOAD_FLAG}:${window.location.pathname}${window.location.search}`;
      // A reload in the last minute already failed to help: show the screen instead.
      if (Date.now() - Number(window.sessionStorage.getItem(key) || 0) < 60_000) { setReloading(false); return; }
      window.sessionStorage.setItem(key, String(Date.now()));
    } catch {
      // Storage blocked: still try a single reload.
    }
    window.location.reload();
  }, [error, isChunkError]);

  if (reloading) return null;

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      padding: "40px 20px",
      textAlign: "center"
    }}>
      <h1 style={{ fontSize: "28px", marginBottom: "16px", fontWeight: 500 }}>
        Something went wrong
      </h1>
      <p style={{ fontSize: "16px", color: "#666", marginBottom: "32px", maxWidth: "500px" }}>
        We couldn&apos;t load this page. Please try again or go back to the homepage.
      </p>
      <div style={{ display: "flex", gap: "16px" }}>
        <button
          onClick={reset}
          style={{
            padding: "12px 28px",
            borderRadius: "24px",
            border: "1px solid #333",
            background: "transparent",
            cursor: "pointer",
            fontSize: "14px",
            fontFamily: "inherit"
          }}
        >
          Try again
        </button>
        <button
          onClick={() => window.location.href = "/"}
          style={{
            padding: "12px 28px",
            borderRadius: "24px",
            border: "none",
            background: "#333",
            color: "#fff",
            cursor: "pointer",
            fontSize: "14px",
            fontFamily: "inherit"
          }}
        >
          Go to Homepage
        </button>
      </div>
    </div>
  );
}
